import React from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import type { LabResult, LabValueFlag } from '../../types/laboklin';

/**
 * The measured values of a report.
 *
 * Out-of-range rows carry a coloured left border, driven by the server-computed
 * flag rather than anything derived in the browser - the same flag the
 * assistant reasons about, so the table and the conversation cannot disagree.
 *
 * Values the laboratory reported qualitatively ("neg.") have no numeric flag and
 * are shown plainly rather than being hidden.
 */

const FLAG_COLOR: Record<LabValueFlag, string | undefined> = {
  high: '#d32f2f',
  low: '#1976d2',
  normal: undefined,
  unknown: undefined,
};

const FLAG_LABEL: Record<LabValueFlag, string> = {
  high: 'Above reference range',
  low: 'Below reference range',
  normal: 'Within reference range',
  unknown: 'Not comparable to a reference range',
};

interface TestResultsTableProps {
  results: LabResult[];
}

export const TestResultsTable: React.FC<TestResultsTableProps> = ({ results }) => {
  if (results.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary', py: 2 }}>
        This report contains no measured values.
      </Typography>
    );
  }

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Parameter</TableCell>
            <TableCell align="right">Value</TableCell>
            <TableCell>Unit</TableCell>
            <TableCell>Reference value</TableCell>
            <TableCell>Material</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {results.map((r) => {
            const color = FLAG_COLOR[r.flag];
            const isAbnormal = r.flag === 'high' || r.flag === 'low';

            return (
              <TableRow
                key={`${r.analyteId}-${r.rawName}`}
                sx={{
                  borderLeft: color ? `3px solid ${color}` : '3px solid transparent',
                  backgroundColor: isAbnormal ? 'rgba(0,0,0,0.02)' : undefined,
                }}
              >
                <TableCell>
                  {/* rawName preserves Laboklin's own "*" markers. */}
                  {r.rawName || r.name}
                  {r.valueText && (
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                      {r.valueText}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={FLAG_LABEL[r.flag]} arrow>
                    <Box
                      component="span"
                      sx={{
                        fontFamily: 'monospace',
                        fontWeight: isAbnormal ? 700 : 400,
                        color: color ?? 'inherit',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {r.rawValue}
                      {isAbnormal && r.foldBeyondLimit !== null && (
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{ ml: 0.75, color, fontWeight: 400 }}
                        >
                          {r.foldBeyondLimit}× {r.flag === 'high' ? 'over' : 'under'}
                        </Typography>
                      )}
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ color: 'text.secondary' }}>{r.unit ?? ''}</TableCell>
                <TableCell sx={{ color: 'text.secondary' }}>{r.referenceRange}</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontSize: 13 }}>{r.material ?? ''}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};

export default TestResultsTable;
